//@author: devin

import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { AngularJSComponentUtil } from "../../util/angularjs.component.util";
import { S25Util } from "../../util/s25-util";
import { PendingObjectService } from "../../services/pending.object.service";
import { EventService } from "../../services/event.service";
import { EventFormModule } from "../event-form/event.form.module";

/**
 * Options for programmatic embedded event-form saves (see save / saveDraft).
 * Object-shaped so callers can opt into future flags without breaking signatures.
 *  - silent: when true, runs the silent shortcut (skips post-save runScenarios + change emails).
 *            Defaults to false so embedded saves match a normal click-to-save by default.
 */
export interface EmbeddedEventFormSaveOptions {
    silent?: boolean;
}

/**
 * Rejection payload shape produced by the 25Live event form's client-side guard paths
 * (validation, quota, conflict, availability, etc). The form already surfaces these
 * failures to the user; hosts use this shape to suppress duplicate toasts and instead
 * branch on `reason` if they want a hint of their own.
 */
export interface EmbeddedEventFormHandledError {
    source: "eventForm";
    reason: string;
}

/** True when the rejection came from the 25Live event form's own handled UX paths. */
export function isEmbeddedEventFormHandledError(error: unknown): error is EmbeddedEventFormHandledError {
    return (
        !!error &&
        typeof error === "object" &&
        (error as { source?: unknown }).source === "eventForm" &&
        typeof (error as { reason?: unknown }).reason === "string"
    );
}

/**
 * Wrapper component for the AngularJS s25EventCreationForm.
 * This allows the AngularJS event creation form to be used within modern Angular components
 * (e.g. embedded in a Connect25 ingestion form).
 *
 * Embedded host (e.g. Connect25) — use explicit flags, not heuristics:
 *  - hideSave: hide the form's built-in submit row so the host owns the button bar
 *  - skipBuiltInPostSaveNavigation: skip post-save redirects / container close (host drives next steps)
 *  - hideAfterSavingThisEventSection: hide the "After Saving This Event..." block when present
 *  - allowProgrammaticSave: attach save / saveDraft on embeddedSaveApi for host-triggered saves
 *  - forceDraft: lock uncommitted / already-Draft events to Draft and hide Event State
 *  - configIdOverride: force a particular form configuration id (otherwise group default)
 *  - eventId / mode='edit': open an existing draft for further edits
 *  - previewOnly: render the read-only s25-ng-event-form-preview after the model hydrates
 *  - save(options?) / saveDraft(options?): programmatic saves that resolve with the event id
 *                        (requires allowProgrammaticSave). Rejects when the 25Live event form
 *                        cannot persist (validation, quota, conflict, availability, failed
 *                        putEvent, etc.). The form owns user-visible messaging for those
 *                        failures — hosts should not re-surface them.
 */
@Component({
    selector: "s25-ng-event-creation-form-wrapper",
    template: `
        <div class="event-creation-form-wrapper">
            <div #formContainer class="form-container" [class.hidden-for-preview]="previewOnly"></div>
            @if (previewOnly && previewModel) {
                <s25-ng-event-form-preview [formModel]="previewModel" [consolidatedOccObj]="true">
                </s25-ng-event-form-preview>
            }
        </div>
    `,
    styles: `
        .event-creation-form-wrapper {
            width: 100%;
            height: 100%;
        }

        .form-container {
            width: 100%;
            min-height: 400px;
        }

        .hidden-for-preview {
            display: none;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [EventFormModule],
})
export class S25NgEventCreationFormWrapperComponent implements OnInit, OnDestroy {
    @ViewChild("formContainer", { static: true }) formContainer!: ElementRef;

    @Input() organizationId?: number;
    @Input() copyEventId?: string;
    @Input() eventId?: number | string;
    @Input() mode: "create" | "edit" = "create";
    @Input() hideSave: boolean = false;
    @Input() configIdOverride?: number;
    @Input() previewOnly: boolean = false;
    /** When true, skip the form's built-in post-save navigation and container close (host owns flow). */
    @Input() skipBuiltInPostSaveNavigation: boolean = false;
    /** When true, hide the "After Saving This Event..." section on the 25Live event form. */
    @Input() hideAfterSavingThisEventSection: boolean = false;
    /** When true, the form exposes save(options?) and saveDraft(options?) on embeddedSaveApi. */
    @Input() allowProgrammaticSave: boolean = false;
    /** When true, lock uncommitted / already-Draft events to Draft and hide Event State. */
    @Input() forceDraft: boolean = false;
    @Input() onFormClose?: () => void;
    @Input() onFormSave?: (eventId: number) => void;

    private angularJSComponentUtil = new AngularJSComponentUtil();
    private componentInstance: any = null;

    /** Regular programmatic save (keeps the event's current state). */
    save = async (saveOptions?: EmbeddedEventFormSaveOptions): Promise<number> => {
        this.assertProgrammaticSaveReady();
        if (!this.embeddedSaveApi.save) {
            throw new Error("Embedded 25Live event form is not ready to save");
        }
        return this.embeddedSaveApi.save(saveOptions);
    };

    /** Draft save: forces Draft when the event is uncommitted or already Draft. */
    saveDraft = async (saveOptions?: EmbeddedEventFormSaveOptions): Promise<number> => {
        this.assertProgrammaticSaveReady();
        if (!this.embeddedSaveApi.saveDraft) {
            throw new Error("Embedded 25Live event form is not ready to save draft");
        }
        return this.embeddedSaveApi.saveDraft(saveOptions);
    };

    // Hydrated form model exposed to the host for occurrence snapshot extraction after save.
    getFormModel = (): any => this.modelRef;

    // For previewOnly mode, exposes the hydrated model to the Angular preview directive.
    protected previewModel: any = null;

    private embeddedSaveApi: {
        save?: (saveOptions?: EmbeddedEventFormSaveOptions) => Promise<number>;
        saveDraft?: (saveOptions?: EmbeddedEventFormSaveOptions) => Promise<number>;
    } = {};
    private modelRef: any = null;

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    ngOnInit() {
        void this.initializeForm();
    }

    ngOnDestroy() {
        if (this.componentInstance) {
            this.componentInstance.destroy();
        }
        this.angularJSComponentUtil.destroyAllComponents();
    }

    private assertProgrammaticSaveReady(): void {
        if (!this.allowProgrammaticSave) {
            throw new Error("Programmatic save is not enabled on this embedded event form");
        }
    }

    /**
     * Initializes the AngularJS event creation form.
     */
    private async initializeForm() {
        try {
            if (!window.angBridge) {
                throw new Error("angBridge is not available");
            }

            const { $q } = window.angBridge;

            // The AngularJS component expects model.modelCallback to be a function
            const model: any = {
                modelCallback: (modelInstance: any) => {
                    const promises: any[] = [];

                    if (this.organizationId) {
                        promises.push(modelInstance.addPrimaryOrganization(this.organizationId));
                    }

                    return $q.all(promises);
                },
            };
            this.modelRef = model;

            // For edit / preview / edit-after-rejection, the host supplies an eventId.
            // For new draft creation, allocate a fresh event id + profile id pair.
            let resolvedEventId: number | string;
            let resolvedProfileId: number;
            if (this.eventId != null) {
                resolvedEventId = this.eventId;
                resolvedProfileId = 0;
            } else {
                const allocated = await EventService.getNewEventProfileId();
                void EventService.primeEventPut(allocated.eventId);
                resolvedEventId = allocated.eventId;
                resolvedProfileId = allocated.profileId;
            }

            const effectiveMode = this.eventId != null && !this.copyEventId ? this.mode || "edit" : this.mode;

            // Prepare bindings for the AngularJS component (see s25-event-creation-form bindings).
            const bindings: Record<string, any> = {
                copyEventId: this.copyEventId || "",
                eventId: resolvedEventId,
                newProfileId: resolvedProfileId,
                mode: effectiveMode,
                model: model,
                apiBean: {
                    broadcastToParentWindow: (data: any) => {
                        if (data?.eventSaved && data.events?.event?.[0]?.event_id) {
                            const savedEventId = parseInt(data.events.event[0].event_id);
                            this.onFormSave?.(savedEventId);
                        }
                    },
                },
                pending: {
                    4: 0,
                    6: 0,
                },
                // Embedded host hooks (explicit booleans → AngularJS "true" / omitted)
                hideSave: this.hideSave ? "true" : null,
                configIdOverride: this.configIdOverride != null ? String(this.configIdOverride) : null,
                previewOnly: this.previewOnly ? "true" : null,
                skipBuiltInPostSaveNavigation: this.skipBuiltInPostSaveNavigation ? "true" : null,
                hideAfterSavingThisEventSection: this.hideAfterSavingThisEventSection ? "true" : null,
                allowProgrammaticSave: this.allowProgrammaticSave ? "true" : null,
                forceDraft: this.forceDraft ? "true" : null,
                embeddedSaveApi: this.embeddedSaveApi,
            };

            try {
                this.componentInstance = await this.angularJSComponentUtil.createAngularJSComponentWithDefer(
                    "s25-event-creation-form",
                    this.formContainer,
                    bindings,
                    "defer",
                );

                // When the AngularJS form's defer resolves, hydration is complete; let the
                // Angular preview render against the now-populated model.
                // The defer chain runs from AngularJS $q (often outside Angular's zone), and hosts
                // such as the Connect25 ingestion form use OnPush — so assign previewModel then
                // run CD on this view explicitly or the @if (previewModel) branch stays stale
                // until some unrelated interaction.
                if (this.previewOnly) {
                    this.previewModel = this.modelRef;
                    this.changeDetectorRef.detectChanges();
                }

                // Override the AngularJS form's close fn so the host (not 25Live) drives lifecycle.
                // previewOnly hides the form UI, so missing close is non-fatal there.
                setTimeout(() => {
                    const controller = this.findEventFormController();
                    if (controller?.close) {
                        controller.close = (closedEventId: any) => {
                            if (closedEventId) {
                                void PendingObjectService.deleteAll(closedEventId, 4);
                                void PendingObjectService.deleteAll(closedEventId, 6);
                            }
                            this.onFormClose?.();
                        };
                    } else if (!this.previewOnly) {
                        console.warn("Could not find event form controller to override close method");
                    }
                }, 100); // Small delay to ensure component is fully initialized
            } catch (caughtError: any) {
                console.error("Event creation form initialization failed:", caughtError);
                S25Util.showError("Failed to initialize event creation form");
            }
        } catch (caughtError) {
            console.error("Failed to initialize event creation form:", caughtError);
            S25Util.showError("Failed to initialize event creation form");
        }
    }

    /**
     * Resolve the AngularJS event-form controller (`controllerAs: "self"`).
     * Prefer the compiled element's isolate scope; fall back to walking child scopes
     * ($$childHead alone is brittle when the parent has other children).
     */
    private findEventFormController(): { close?: (eventId?: unknown, skipRedirects?: unknown) => void } | null {
        const instance = this.componentInstance;
        if (!instance) return null;

        const compiledElement = instance.element;
        const isolateScope =
            compiledElement?.isolateScope?.() ??
            (compiledElement?.[0] ? window.angular?.element(compiledElement[0]).isolateScope?.() : undefined);
        if (isolateScope?.self?.close) return isolateScope.self;

        let childScope = instance.scope?.$$childHead;
        while (childScope) {
            if (childScope.self?.close) return childScope.self;
            childScope = childScope.$$nextSibling;
        }

        return instance.scope?.self?.close ? instance.scope.self : null;
    }
}
